import type { DetectedObject } from '@/entities/brand-media';

import { boxColour } from '../lib/box-colours';
import { projectBox } from '../lib/box-projection';

/**
 * The detected boxes drawn over whatever is showing the picture.
 *
 * Takes the frame's own geometry rather than measuring it, so the same component serves the panel's
 * cropped thumbnail and the dialog's letterboxed full view without either having to wait for a
 * layout pass to know where to draw.
 */
export const BoundingBoxOverlay = ({
  objects,
  drawn,
  emphasised = null,
  imageAspect,
  elementAspect,
  fit,
}: {
  objects: readonly DetectedObject[];
  /** Which of them to draw. Indices, because the colour and the row's chip key off position. */
  drawn: readonly number[];
  /** Drawn heavier than the rest. The one the pointer is on, when there is one. */
  emphasised?: number | null;
  imageAspect: number;
  elementAspect: number;
  fit: 'cover' | 'contain';
}) => (
  // Ignores the pointer throughout: hovering a box must not take the hover off the row that
  // summoned it, which would leave the two fighting and the box flickering.
  <div className="pointer-events-none absolute inset-0" aria-hidden="true">
    {drawn.map((index) => {
      // `at` rather than an index read, because the answer genuinely can be nothing: `drawn` is a
      // list of positions and its caller's state can outlive the list it indexes. A plain read
      // types as always-present and hands `undefined` to `projectBox`, which dereferences it.
      const object = objects.at(index);
      if (object === undefined) {
        return null;
      }

      const { left, top, width, height } = projectBox(object, imageAspect, elementAspect, fit);
      const colour = boxColour(index);
      const lit = emphasised === index;

      return (
        <div
          key={index}
          data-testid={`media-box-${index}`}
          data-emphasised={lit ? 'true' : undefined}
          className="absolute box-border"
          style={{
            left: `${left}%`,
            top: `${top}%`,
            width: `${width}%`,
            height: `${height}%`,
            border: `${lit ? 3 : 2}px solid ${colour}`,
            // The fill is what makes a box readable over a busy photograph; the emphasised one is
            // filled harder rather than recoloured, so it keeps the colour its row is labelled with.
            backgroundColor: `${colour}${lit ? '59' : '2e'}`,
          }}
        >
          {/* Inside the box rather than above it. A label hung over the top edge is clipped away
              for anything detected near the top of the frame, which is most subjects. */}
          <span
            className="absolute left-0 top-0 max-w-full truncate px-1 py-0.5 text-label-xs font-semibold leading-none text-white"
            style={{ backgroundColor: colour }}
          >
            {object.name}
          </span>
        </div>
      );
    })}
  </div>
);
